
import { Component, Prop, Vue, Watch } from "vue-property-decorator"
import FormField from "../../base_classes/FormField"
import { v4 as generateUuid } from "uuid"
import workReportTemplates from "@/graphql/workreport/workreporttemplates.graphql"
import jobTemplatesQuery from "@/graphql/job/jobtemplates.graphql"
import * as cdn from "@coredination/components"
import GroupsQuery from "@/graphql/group/groups.graphql"

@Component({
    components: {
        SelectTemplateDialog: () => import("@/components/dialogs/SelectTemplateDialog.vue"),
    },
    apollo: {
        workReportTemplates: {
            query: workReportTemplates,
            skip() {
                return this.entity !== "workReport" && this.entity !== "absenceReport"
            },
        },
        me: cdn.graphql.me,
        groups: {
            query: GroupsQuery,
            variables() {
                return { inactive: true }
            },
        },
        jobTemplates: {
            query: jobTemplatesQuery,
            skip() {
                return this.entity !== "job"
            },
        },
    },
})
export default class TemplateSelectField extends FormField {
    showDialog: boolean = false
    selectedTemplate: any = null
    workReportTemplates?: any[]
    jobTemplates?: any[]
    me: any = {}
    groups?: any[]

    @Prop()
    entity?: string

    @Prop({type: Boolean, default: false})
    applyDefault!: boolean

    @Prop()
    setResourceAssetFromParams?: boolean

    get model() {
        return this.value
    }
    set model(value: any) {
        this.value = value
    }

    get computedTemplate() {
        return this.selectedTemplate
            ? this.selectedTemplate.title
            : this.model.fromTemplateId && this.findTemplate(this.model.fromTemplateId)
            ? this.findTemplate(this.model.fromTemplateId).title
            : null
    }
    set computedTemplate(value: any) {
        this.selectedTemplate = value
    }

    handleSelection(event: any) {
        this.selectedTemplate = event

        var newTemplateContent: any = JSON.parse(JSON.stringify(this.selectedTemplate))
        delete newTemplateContent.id
        delete newTemplateContent.uuid
        delete newTemplateContent.title
        newTemplateContent.fromTemplateId = event.id
        newTemplateContent.requiredFields = event.requiredFields

        // If we should always create in primary group then set that as the group
        if (this.me && this.me.shareAllWithPrimaryGroup) {
            newTemplateContent.groupUuid = this.me.primaryGroupUuid
            newTemplateContent.groupName = this.me.primaryGroupName
        } else {
            var templateGroup = this.getGroupMembership(newTemplateContent.groupUuid)
            if (this.entity === "job") {
                // Don't change group if we don't have permission to create jobs in that group
                if (!templateGroup || !templateGroup.permissions || !templateGroup.permissions.includes("create_jobs")) {
                    newTemplateContent.groupUuid = this.model.groupUuid
                    newTemplateContent.groupName = this.model.groupName
                }
            } else if (this.entity === "workReport" || this.entity === "absenceReport") {
                // If we could not find the template group then we don't have a membership to that group and should retain the current group
                if (!templateGroup) {
                    newTemplateContent.groupUuid = this.model.groupUuid
                    newTemplateContent.groupName = this.model.groupName
                }
            }
            // Retain current group if the templates group is the same as current group or if the template is from a parent group
            if (newTemplateContent.groupUuid && this.isValidForGroup(newTemplateContent.groupUuid)) {
                newTemplateContent.groupUuid = this.model.groupUuid
                newTemplateContent.groupName = this.model.groupName
            }
        }

        if (newTemplateContent.items) {
            newTemplateContent.items.forEach((item: any) => {
                delete item.id
                item.uuid = generateUuid()
            })
        }

        // dont let job template query overwrite predefined values when creating job from asset, customer etc.
        const params: any = {
            ...this.$route.params,
            ...this.$route.query,
        }
        if ('asset' in params) {
            if (this.setResourceAssetFromParams && this.model.resources && this.model.resources[0]) {
                if (!newTemplateContent.resources) {
                    newTemplateContent.resources = this.model.resources
                } else if (!newTemplateContent.resources[0]) {
                    newTemplateContent.resources[0] = this.model.resources[0]
                } else if (!newTemplateContent.resources[0].assets) {
                    newTemplateContent.resources[0].assets = this.model.resources[0].assets
                } else {
                    this.model.resources[0].assets.forEach((asset: any) => {
                        newTemplateContent.resources[0].assets.push(asset)
                    })
                }
            }
            newTemplateContent.assetName = this.model.assetName
            newTemplateContent.assetNo = this.model.assetNo
            newTemplateContent.assetUuid = this.model.assetUuid
        }
        if ('customer' in params) {
            newTemplateContent.customerUuid = this.model.customerUuid
            newTemplateContent.customerName = this.model.customerName
            newTemplateContent.customerNo = this.model.customerNo
        }
        if ('customerproject' in params) {
            newTemplateContent.customerProjectUuid = this.model.customerProjectUuid
            newTemplateContent.customerProjectName = this.model.customerProjectName
            newTemplateContent.customerProjectNo = this.model.customerProjectNo
        }
        this.$emit("input", newTemplateContent)
    }

    handleExistingTemplate(item: any) {
        this.selectedTemplate = item
    }

    findTemplate(templateId: number) {
        switch (this.entity) {
            case "workReport":
            case 'absenceReport':
                return (
                    this.workReportTemplates && this.workReportTemplates.find((template) => template.id === templateId)
                )
            case "job":
                return this.jobTemplates && this.jobTemplates.find((template) => template.id === templateId)
            default:
                return null
        }
    }

    @Watch("groups")
    @Watch("model.groupUuid")
    @Watch("applyDefault")
    findDefaultTemplateId() {
        if (this.selectedTemplate || !this.entity || !this.applyDefault) return
        if (this.model.groupUuid && this.groups) {
            var group: any = this.groups.find((group) => group.uuid == this.model.groupUuid)
            var defaultTemplateIdName = this.getTemplateIdName
            if (!group || !group[defaultTemplateIdName]) return
            var defaultTemplate = this.findTemplate(group[defaultTemplateIdName])
            if (defaultTemplate) this.handleSelection(defaultTemplate)
        }
    }

    get loading() {
        return (
            this.$apollo.queries.workReportTemplates.loading ||
            this.$apollo.queries.jobTemplates.loading ||
            this.$apollo.queries.groups.loading
        )
    }

    get getTemplateIdName() {
        switch (this.entity) {
            case "workReport":
                return "defaultWorkReportTemplateId"
            case "job":
                return "defaultJobTemplateId"
            default:
                return ""
        }
    }
    clickClear() {
        this.computedTemplate = null
    }

    getGroupMembership(groupUuid: string) {
        if (this.me && this.me.groupMemberships) {
            return this.me.groupMemberships.find((membership: any) => {
                return membership.groupUuid === groupUuid
            })
        }
        return null
    }

    isValidForGroup(groupUuid: string) {
        if (!this.groups) return false
        var group = this.groups.find(({ uuid }) => uuid === this.model.groupUuid);
        if (group && group.uuid === groupUuid) return true
        var count = 0;
        while (group && count < 1000) {
            if (group && group.parentGroupUuid && group.parentGroupUuid === groupUuid) return true;
            group = this.groups.find(({ uuid }) => uuid === group.parentGroupUuid);
            count++
        };
       return false
    }
}
